import type { Node, NodeType, ResolvedPos } from '@tiptap/pm/model';
import { type EditorState, Plugin, PluginKey } from '@tiptap/pm/state';
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list';

function isList(node: Node | null | undefined): node is Node {
   return !!node && node.type.isInGroup('list');
}

function listAround($pos: ResolvedPos) {
   for (let depth = $pos.depth; depth > 0; depth--) {
      const node = $pos.node(depth);
      if (isList(node)) {
         return { node, pos: $pos.before(depth) };
      }
   }
   return null;
}

/*
   editor.isActive('bulletList') is true anywhere inside a bullet list, so in a
   bullet sublist of a numbered list both list buttons would light up.
*/
export function isInnermostList(state: EditorState, listName: string) {
   const list = listAround(state.selection.$from);
   return list !== null && list.node.type.name === listName;
}

/*
   Toggling off the list a selection starts in lifts every selected item, so a
   selection running from a sublist into its parent list (or the reverse)
   unwraps both levels at once. Switching type only retypes the list where the
   selection starts, so leave that alone and refuse just the lift.
*/
function wouldLiftAcrossLists(state: EditorState, listType: NodeType) {
   const startList = listAround(state.selection.$from);
   const endList = listAround(state.selection.$to);
   return (
      !!startList && !!endList && startList.pos !== endList.pos && startList.node.type === listType
   );
}

export const CustomBulletList = BulletList.extend({
   addCommands() {
      const { toggleBulletList } = this.parent!();
      return {
         toggleBulletList: () => props =>
            !wouldLiftAcrossLists(props.state, this.type) && toggleBulletList!()(props),
      };
   },
});

export const CustomOrderedList = OrderedList.extend({
   addCommands() {
      const { toggleOrderedList } = this.parent!();
      return {
         toggleOrderedList: () => props =>
            !wouldLiftAcrossLists(props.state, this.type) && toggleOrderedList!()(props),
      };
   },
});

/*
   Wikitext gives a list item at most one sublist: the parser
   (Objects/Text/Wiki/Parse/Ifixit/List.php) types a nested run by its first
   line, so `** note` followed by `## step` reloads as two bullets. Tab
   (sinkListItem) builds exactly that shape when the item above already has a
   sublist of the other type, so fold any sublist into the one before it,
   taking that list's type, so the editor shows what a reload would.
*/
export const CustomListItem = ListItem.extend({
   addProseMirrorPlugins() {
      const listItemType = this.type;

      return [
         ...(this.parent?.() ?? []),
         new Plugin({
            key: new PluginKey('mergeSiblingSublists'),
            appendTransaction(transactions, _oldState, newState) {
               if (transactions.every(tr => !tr.docChanged)) {
                  return null;
               }

               const sublistPositions: number[] = [];
               newState.doc.descendants((node, pos) => {
                  if (node.isTextblock) {
                     return false;
                  }
                  if (node.type === listItemType) {
                     node.forEach((child, offset, index) => {
                        if (index > 0 && isList(child) && isList(node.child(index - 1))) {
                           sublistPositions.push(pos + 1 + offset);
                        }
                     });
                  }
                  return true;
               });

               if (!sublistPositions.length) {
                  return null;
               }

               const tr = newState.tr;
               // Back to front, so each merge leaves earlier positions valid
               // and a run of three sublists collapses into the first. The
               // walk visits an item before its nested items, so its order
               // isn't document order once two levels both need merging.
               for (const pos of sublistPositions.sort((a, b) => b - a)) {
                  const previous = tr.doc.resolve(pos).nodeBefore!;
                  tr.setNodeMarkup(pos, previous.type, previous.attrs);
                  tr.join(pos);
               }
               return tr;
            },
         }),
      ];
   },
});
